import { t } from '@shared/helpers/translation';
import { convertToDate, getShortDate, isBefore } from '@shared/helpers/datetime/datetime';
import {
    ActivityProduct,
    AnyBookingProduct,
    FerryLeg,
    FerryProduct,
    FlightLeg,
    FlightProduct,
    FlightType,
    HotelProduct,
    ProductType,
    TransferLocation,
    TransferProduct,
} from '@/api/MyBooking/myBooking.interfaces';
import { capitalize, isEmpty } from 'lodash';
import { LabelFormat } from '@shared/interfaces/translation';
import { Type as IconType } from '@ui/components/Icon/Icon.interfaces';
import { getLocaleDayShortMonth } from '@shared/helpers/datetime/datetime.formatters';
import { getValueOrFallback } from '@shared/helpers/getValueOrFallback';

export function getProductTitle(type: ProductType, format?: LabelFormat) {
    switch (type) {
        case ProductType.Flight:
            return t({ key: 'shared.flight_product', format });
        case ProductType.Ferry:
            return t({ key: 'shared.ferry_product', format });
        case ProductType.Hotel:
            return t({ key: 'shared.hotel_product', format });
        case ProductType.Golf:
            return t({ key: 'shared.golf_product', format });
        case ProductType.Activity:
            return t({ key: 'shared.activity_product', format });
        case ProductType.Transfer:
            return t({ key: 'shared.transfer_product', format });
        case ProductType.CustomBaggage:
            return t({ key: 'shared.custom_baggage_product', format });
        default:
            return '';
    }
}

export function getProductIcon(type: ProductType): IconType | undefined {
    switch (type) {
        case ProductType.Hotel:
            return 'Bed';
        case ProductType.Flight:
            return 'Plane';
        case ProductType.Ferry:
            return 'Ferry';
        case ProductType.Activity:
            return 'Ticket';
        default:
            return undefined;
    }
}

export function getProductDescriptions(p: AnyBookingProduct) {
    switch (p.type) {
        case ProductType.Flight:
            return getFlightDescriptions(p as FlightProduct);
        case ProductType.Hotel:
        case ProductType.Golf:
            return getHotelDescriptions(p as HotelProduct);
        case ProductType.Ferry:
            return getFerryDescriptions(p as FerryProduct);
        case ProductType.Activity:
            return getActivityDescriptions(p as ActivityProduct);
        case ProductType.Transfer:
            return getTransferDescriptions(p as TransferProduct);
        default:
            return [];
    }
}

export function getFlightDescriptions({ legs }: FlightProduct): string[] {
    const isOneWay = legs[1] === undefined;

    const routeText = getFlightRouteText(legs);

    const dateText = isOneWay ? getDepartureDateAndType(legs[0]) : getRangeDateAndType(legs);

    const result = [];
    if (routeText) result.push(routeText);
    if (dateText) result.push(dateText);
    return result;
}

function getHotelDescriptions({ address, fromDate }: HotelProduct): string[] {
    const items = [];

    if (address?.city) {
        items.push(capitalize(address.city));
    }

    return [...items, getLocaleDayShortMonth(convertToDate(fromDate))];
}

export function getFerryDescriptions({ legs }: FerryProduct) {
    const isOneWay = legs[1] === undefined;

    const outbound = legs[0];
    const segment = outbound.segments[0];

    const dateText = isOneWay
        ? getDepartureDateAndType(legs[0])
        : getLocaleDayShortMonth(convertToDate(segment.departureDate));

    return [segment.legName, dateText];
}

function getActivityDescriptions({ placeName, date }: ActivityProduct): string[] {
    return [placeName, getLocaleDayShortMonth(convertToDate(date))];
}

function getTransferDescriptions({ from, to }: TransferProduct) {
    const items = [];

    const label = getTransferLabels(from, to);

    if (label) {
        items.push(label);
    }

    if (from.dateTime) {
        items.push(getLocaleDayShortMonth(convertToDate(from.dateTime)));
    }

    return items;
}

function getDepartureDateAndType(leg: FlightLeg | FerryLeg) {
    const segment = leg.segments[0];

    return segment.departureDate
        ? getLocaleDayShortMonth(convertToDate(segment.departureDate)).concat(
              t({ key: 'shared.one-way', prefix: ' (', suffix: ')' })
          )
        : '';
}

function getRangeDateAndType(legs: FlightLeg[] | FerryLeg[]) {
    const outboundSegment = legs[0].segments[0];
    const homeboundSegment = legs[1].segments[0];

    const result = [];
    if (outboundSegment.departureDate) {
        result.push(getLocaleDayShortMonth(convertToDate(outboundSegment.departureDate)));
    }
    if (outboundSegment.departureDate && homeboundSegment.departureDate) {
        result.push(getLocaleDayShortMonth(convertToDate(homeboundSegment.departureDate)));
    }
    return result
        .join(' - ')
        .concat(t({ key: 'shared.roundtrip', prefix: ' (', suffix: ')' }));
}

export function getFlightRouteText(legs: FlightLeg[]): string {
    const outbound = legs[0];
    const firstSegment = outbound.segments[0];
    const lastSegment = outbound.segments[outbound.segments.length - 1];
    const flightDescriptionError = `[BookingPage] getFlightRouteText() ${firstSegment.departureAirportCode} ${lastSegment.arrivalAirportCode}`;
    const fallback = '';

    const departureName = getValueOrFallback(
        firstSegment.departureCityName,
        fallback,
        flightDescriptionError
    );
    const arrivalName = getValueOrFallback(
        lastSegment.arrivalCityName,
        fallback,
        flightDescriptionError
    );

    return `${departureName} - ${arrivalName}`;
}

export const getFlightRouteSummary = (
    legs: FlightLeg[]
): { from: string; to: string; label: string } | null => {
    const outbound = legs[0];
    const firstSegment = outbound.segments[0];
    const lastSegment = outbound.segments[outbound.segments.length - 1];
    const departureCode = firstSegment.departureAirportCode;
    const arrivalCode = lastSegment.arrivalAirportCode;

    return departureCode && arrivalCode
        ? {
              from: departureCode,
              to: arrivalCode,
              label: `${departureCode} - ${arrivalCode}`,
          }
        : null;
};

export function getLocationLabel(location: TransferLocation) {
    if (location.iataInfo?.code) {
        return location.iataInfo.labelShort
            ? location.iataInfo.labelShort
            : location.iataInfo.code;
    } else if (location.address) {
        return location.address;
    }

    return '';
}

function getTransferLabels(from: TransferLocation, to: TransferLocation) {
    const fromLabel = getLocationLabel(from);
    const toLabel = getLocationLabel(to);

    if (fromLabel && toLabel) {
        return `${fromLabel} - ${toLabel}`;
    }

    return '';
}

export function expiredProduct(p: AnyBookingProduct): boolean {
    if (p) {
        const date = getProductToDate(p);

        if (date) {
            const today = convertToDate(getShortDate(new Date()));
            return isBefore(date, today);
        }

        return true;
    }

    return false;
}

export function sortProducts(products: AnyBookingProduct[]) {
    if (products && !isEmpty(products)) {
        return [...products].sort((a: AnyBookingProduct, b: AnyBookingProduct) => {
            const dateA = getProductFromDate(a);
            const dateB = getProductFromDate(b);

            const priority = getProductPriority(a) - getProductPriority(b);

            if (dateA === null && dateB === null) {
                return priority;
            } else if (dateA === null) {
                return 1;
            } else if (dateB === null) {
                return -1;
            } else {
                const isSame = dateA.valueOf() === dateB.valueOf();
                return isSame ? priority : dateA.valueOf() - dateB.valueOf();
            }
        });
    }

    return [];
}

function getProductPriority(p: AnyBookingProduct): number {
    switch (p.type) {
        case ProductType.Flight:
            return 0;
        case ProductType.Ferry:
            return 1;
        case ProductType.Transfer:
            return 2;
        case ProductType.Hotel:
            return 3;
        case ProductType.Activity:
            return 4;
        case ProductType.Golf:
            return 5;
        default:
            return 99;
    }
}

function getProductFromDate(p: AnyBookingProduct): Date | null {
    let date = undefined;

    switch (p.type) {
        case ProductType.Flight: {
            const departure = p.legs[0].segments[0].departureDate;
            date = departure ? convertToDate(departure) : null;
            break;
        }
        case ProductType.Ferry:
            date = convertToDate(p.legs[0].segments[0].departureDate);
            break;
        case ProductType.Hotel:
        case ProductType.Golf:
            date = convertToDate(p.fromDate);
            break;
        case ProductType.Transfer:
            date = p.from.dateTime ? convertToDate(p.from.dateTime) : null;
            break;
        case ProductType.Activity:
            date = convertToDate(p.date);
            break;
    }

    return date ? convertToDate(getShortDate(date)) : null;
}

export function getProductToDate(p: AnyBookingProduct): Date | null {
    let date = undefined;

    if (!p) {
        return null;
    }

    switch (p.type) {
        case ProductType.Flight: {
            const leg = p.legs[p.legs.length - 1];
            const segment = leg.segments[leg.segments.length - 1];
            date = segment.arrivalDate ? convertToDate(segment.arrivalDate) : null;
            break;
        }
        case ProductType.Ferry: {
            const leg = p.legs[p.legs.length - 1];
            const segment = leg.segments[leg.segments.length - 1];
            date = segment.arrivalDate ? convertToDate(segment.arrivalDate) : null;
            break;
        }
        case ProductType.Hotel:
        case ProductType.Golf:
            date = convertToDate(p.toDate);
            break;
        case ProductType.Transfer:
            date = p.to.dateTime ? convertToDate(p.to.dateTime) : null;
            break;
        case ProductType.Activity:
            date = convertToDate(p.date);
            break;
    }

    return date ? convertToDate(getShortDate(date)) : null;
}

export function bookingHasRyanair(products: FlightProduct[]): boolean {
    return products.some((p) => bookingProductHasRyanair(p));
}

export function bookingProductHasRyanair(product: FlightProduct): boolean {
    return product.ticket.type === FlightType.Ryanair;
}
